import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import process from 'process';
import { useCreateUpdateScenarioMutation } from '../services/edgeApi';
import { Scenario } from '../types/api';
import toast from 'react-hot-toast';

const CONNECTION_TIMEOUT = 10 * 1000;
const KEEP_ALIVE_INTERVAL = 30 * 1000;
const KEEP_ALIVE_TIMEOUT = 10 * 1000;

export type OptimizationTaskState =
  | 'PENDING'
  | 'STARTED'
  | 'SUCCESS'
  | 'ERROR'
  | 'FAILED';

export enum OptimizationMessageTypes {
  UPDATE = 'UPDATE',
  ERROR = 'ERROR',
  PROGRESS = 'PROGRESS',
  PING = 'PING',
}

export type OptimizationProgressPayload = {
  percent: number;
  step: string;
  total_steps: number;
  completed_steps: number;
};

export type OptimizationMessage = {
  type: OptimizationMessageTypes;
};

export type OptimizationUpdateMessage = OptimizationMessage & {
  status: OptimizationTaskState;
  state: OptimizationTaskState;
  message: string;
};

export type OptimizationErrorResponse = OptimizationUpdateMessage;

export type OptimizationProgressResponse = OptimizationMessage & {
  progress: OptimizationProgressPayload;
};

type StopFunction = () => void;

export type UseAsyncOptimizeScenarioReturn = {
  reconnect: () => void;
  watch: () => StopFunction | undefined;
  optimize: () => void;
  saveAndOptimize: (payload: Partial<Scenario>) => Promise<Scenario>;
  ready: boolean;
  socketError: boolean;
  status: OptimizationTaskState;
  state: OptimizationTaskState;
  message: string;
  optimizationProgress: OptimizationProgressPayload | null;
  optimizationError: string | null;
  isLoading: boolean;
};

export const useAsyncOptimizeScenario = (
  scenarioId: number | string,
  options: { pingInterval?: number; skip?: boolean } = {
    pingInterval: 30,
    skip: false,
  },
): UseAsyncOptimizeScenarioReturn => {
  const socket: React.MutableRefObject<WebSocket | null> = useRef(null);
  const interval: React.MutableRefObject<NodeJS.Timer | null> = useRef(null);
  const { pingInterval = 30, skip = false } = options;

  const [saveScenarioTrigger] = useCreateUpdateScenarioMutation();

  const [socketError, setSocketError] = useState(false);
  const [optimizationError, setOptimizationError] = useState<string | null>(
    null,
  );
  const [status, setStatus] = useState<OptimizationTaskState>('PENDING');
  const [state, setState] = useState<OptimizationTaskState>('PENDING');
  const [message, setMessage] = useState('');
  const [optimizationProgress, setOptimizationProgress] =
    useState<OptimizationProgressPayload | null>(null);

  const connectionTimeout = useRef<NodeJS.Timeout | null>(null);
  const keepAliveInterval = useRef<NodeJS.Timeout | null>(null);
  const keepAliveTimeout = useRef<NodeJS.Timeout | null>(null);

  const keepAlive = useCallback(() => {
    if (keepAliveTimeout.current !== null)
      clearTimeout(keepAliveTimeout.current);
    keepAliveTimeout.current = setTimeout(() => {
      if (socket.current?.readyState === WebSocket.OPEN) {
        socket.current?.close();
        toast.error('Error: optimization server stopped responding');
        setState('ERROR');
        setStatus('ERROR');
        setMessage('Optimization server stopped responding');
      }
    }, KEEP_ALIVE_TIMEOUT);
  }, [socket]);

  const handleUpdate = useCallback((message: OptimizationUpdateMessage) => {
    setStatus(message.status);
    setState(message.state);
    setMessage(message.message);
    if (message.state === 'ERROR') {
      setOptimizationError(message.message);
    }
    if (message.state === 'STARTED') {
      setOptimizationError(null);
    }
  }, []);

  const handleProgress = useCallback(
    (message: OptimizationProgressResponse) => {
      setOptimizationProgress(message.progress);
    },
    [],
  );

  const handleMessage = useCallback((message: OptimizationMessage) => {
    if (message.type === OptimizationMessageTypes.UPDATE) {
      handleUpdate(message as OptimizationUpdateMessage);
    }
    if (message.type === OptimizationMessageTypes.ERROR) {
      handleUpdate(message as OptimizationUpdateMessage);
    }
    if (message.type === OptimizationMessageTypes.PROGRESS) {
      handleProgress(message as OptimizationProgressResponse);
    }
    if (message.type === OptimizationMessageTypes.PING) {
      clearTimeout(keepAliveTimeout.current!);
    }
  }, []);

  const connect = useCallback(
    (id: string | null = null) => {
      if (skip) return null;
      connectionTimeout.current = setTimeout(() => {
        toast.error("Couldn't connect to the optimization server");
        setState('ERROR');
        setStatus('ERROR');
        setMessage("Couldn't connect to the optimization server");
      }, CONNECTION_TIMEOUT);
      const token = localStorage.getItem('token');
      socket.current = new WebSocket(
        `${
          process.env.REACT_APP_STREAMING_API_URL
        }/ws/scenario/?access_token=${token}&scenario_id=${id ?? scenarioId}`,
      );

      if (keepAliveInterval.current !== null) {
        clearInterval(keepAliveInterval.current);
      }
      keepAliveInterval.current = setInterval(() => {
        socket.current?.send(JSON.stringify({ action: 'ping' }));
        keepAlive();
      }, KEEP_ALIVE_INTERVAL);

      socket.current.onmessage = (event) => {
        const message = JSON.parse(event.data) as OptimizationMessage;
        handleMessage(message);
      };

      socket.current.onerror = (_event) => {
        setSocketError(true);
      };

      socket.current.onopen = () => {
        if (connectionTimeout.current !== null) {
          clearTimeout(connectionTimeout.current);
        }
        setSocketError(false);
        socket.current?.send(JSON.stringify({ action: 'get' }));
      };

      socket.current.onclose = (_event) => {
        if (keepAliveInterval.current !== null)
          clearInterval(keepAliveInterval.current);
        if (keepAliveTimeout.current !== null)
          clearTimeout(keepAliveTimeout.current);
      };

      return socket.current;
    },
    [scenarioId, keepAlive, handleMessage, skip],
  );

  const ready = useMemo(
    () => socket.current?.readyState === WebSocket.OPEN,
    [socket.current?.readyState],
  );

  const reconnect = useCallback(() => {
    connect();
  }, [connect, socket]);

  const watch = useCallback(() => {
    if (skip) return;
    if (ready === false) {
      console.warn('Socket is not open');
      return;
    }
    const it = setInterval(() => {
      socket.current?.send(JSON.stringify({ action: 'get' }));
    }, pingInterval * 1000);
    const stop = () => clearInterval(it);
    interval.current = it;
    return stop;
  }, [socket, ready, pingInterval]);

  const optimize = useCallback(() => {
    if (ready === false) {
      console.warn('Socket is not open');
      return;
    }
    socket.current?.send(JSON.stringify({ action: 'optimize' }));
  }, [socket, ready, pingInterval]);

  const saveAndOptimize = useCallback(
    async (payload: Partial<Scenario>) => {
      try {
        if (skip) {
          throw new Error('Skipping optimization as skip option is enabled');
        }
        setState('STARTED');
        setStatus('STARTED');
        setMessage('Saving scenario...');
        const result = await saveScenarioTrigger({
          ...payload,
          optimize: false,
          multiCycle: false,
        }).unwrap();
        if (!result) {
          throw new Error('Error saving scenario');
        }
        const id = result?.id;
        const newSocket = connect(id);
        newSocket?.addEventListener('open', () =>
          newSocket?.send(JSON.stringify({ action: 'optimize' })),
        );
        return result;
      } catch (error) {
        setState('ERROR');
        setStatus('ERROR');
        setMessage('Error saving scenario');
        throw error;
      }
    },
    [saveScenarioTrigger, connect, socket.current, skip],
  );

  useEffect(() => {
    if (scenarioId !== 'new') {
      connect();
    }
  }, [scenarioId]);

  useEffect(() => {
    const oldSocket = socket.current;
    return () => {
      if (oldSocket?.readyState === WebSocket.OPEN) {
        oldSocket.close();
      }
    };
  }, [socket.current]);

  return {
    reconnect,
    watch,
    optimize,
    saveAndOptimize,
    ready,
    socketError,
    status,
    state,
    message,
    optimizationProgress,
    optimizationError,
    isLoading: status === 'STARTED',
  };
};
